import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { AllergyIntolerance } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemHeader, ItemTitle } from '../ui/item';

export type AllergyDetailProps = {
  allergy: WithId<AllergyIntolerance>;
  onBack?: () => void;
};

export const AllergyDetail = ({ allergy, onBack }: AllergyDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const items = [
    {
      title: t('status'),
      value: allergy.clinicalStatus ? formatCodeableConcept(allergy.clinicalStatus) : t('unknown'),
    },
    {
      title: t('verification'),
      value: allergy.verificationStatus ? formatCodeableConcept(allergy.verificationStatus) : t('unknown'),
    },
    {
      title: t('onsetDate'),
      value: allergy.onsetDateTime ? format(parseISO(allergy.onsetDateTime), DATE_FORMATS.DATE) : t('unknown'),
    },
    {
      title: t('recordedDate'),
      value: allergy.recordedDate ? format(parseISO(allergy.recordedDate), DATE_FORMATS.DATE) : t('unknown'),
    },
    { title: t('category'), value: allergy.category?.at(0) ?? t('unknown') },
    {
      title: t('reactions'),
      value: allergy.reaction?.map((reaction) => formatCodeableConcept(reaction.manifestation?.at(0))).join(', '),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      {onBack && (
        <Button id="back-to-allergies-button" variant="ghost" onClick={onBack} className="mb-4 flex items-center gap-2">
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToAllergies')}</span>
        </Button>
      )}

      <Item>
        <ItemHeader className="flex flex-col items-start gap-2">
          <div className="flex justify-center gap-2">
            <Badge>{allergy.clinicalStatus ? formatCodeableConcept(allergy.clinicalStatus) : t('unknown')}</Badge>
          </div>

          <ItemTitle className="text-lg font-medium">
            {allergy.code ? formatCodeableConcept(allergy.code) : t('unknownAllergy')}
          </ItemTitle>
        </ItemHeader>
        <ItemContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {items.map((item) => (
              <div key={item.title}>
                <div className="mb-1 text-muted-foreground">{item.title}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>
        </ItemContent>
      </Item>
    </div>
  );
};
